import {
  Order,
  OrderEntry,
  QuickbooksUpdate,
  QuickbooksUpdateRepository,
  QuickbooksUpdateStatusEnum,
  QuickbooksUpdateTypeEnum,
  GLCodeProductCategoryRepository,
  ProductCategoryCollection
} from '..'
import * as firebase from 'firebase'

export class QuickbooksUpdateService {
  private productCategoryCollection: ProductCategoryCollection

  constructor(
      protected restaurantId: string,
      protected db: firebase.firestore.Firestore,
      protected repo: QuickbooksUpdateRepository = new QuickbooksUpdateRepository()
  ) {
    this.productCategoryCollection = new ProductCategoryCollection(db)
  }

  public async sendInventory(entry: OrderEntry) {
    const updates = await this.repo.findPreviousUpdates(entry.id, QuickbooksUpdateTypeEnum.INVENTORY_ADD, this.restaurantId)
    const meta = this.consolidateMetadata(updates)

    meta.name = entry.description
    meta.barcode = entry.upc
    meta.vendorId = entry.vendorCompanyId

    const productCategoryId = await this.getProductCategoryId(entry.category || '')
    const glCode = productCategoryId ? await this.getGLCodeById(productCategoryId) : ''
    meta.glCode = glCode

    meta.pricePerUnit = this.adjustUpdateValue(entry.pricePerUnit, meta.pricePerUnit)
    meta.quantity = this.adjustUpdateValue(entry.receivedQuantity, meta.quantity)

    const update = new QuickbooksUpdate()
    update.type = QuickbooksUpdateTypeEnum.INVENTORY_ADD
    update.typeId = entry.id
    update.createdAt = new Date()
    update.status = QuickbooksUpdateStatusEnum.PENDING
    update.restaurantId = this.restaurantId
    update.meta = meta
    update.summary = `${ parseInt(meta.quantity) > 0 ? 'Adding' : 'Removing' } ${ Math.abs(parseInt(meta.quantity)) } ${ meta.name }`

    return this.repo.save(update)
  }

  public async sendPurchaseOrder(order: Order) {
    // todo - make sure this is sent on vendor order only, not resturant order

    // todo - uncomment when we pick this back up
    // const updates = await this.repo.findPreviousUpdates(order.id, QuickbooksUpdateTypeEnum.PURCHASE_ORDER_ADD, this.restaurantId)
    // const meta = this.consolidateMetadata(updates)

    // meta.vendor = '' // todo: put this in
    // meta.meta.vendorId = entry.vendorCompanyId

    // meta.pricePerUnit = this.adjustUpdateValue(entry.pricePerUnit, meta.pricePerUnit)
    // meta.quantity = this.adjustUpdateValue(entry.receivedQuantity, meta.quantity)

    // const update = new QuickbooksUpdate()
    // update.type = QuickbooksUpdateTypeEnum.PURCHASE_ORDER_ADD
    // update.typeId = entry.id
    // update.createdAt = new Date()
    // update.status = QuickbooksUpdateStatusEnum.PENDING
    // update.restaurantId = this.restaurantId
    // update.meta = meta
    // update.summary = `${ meta.vendor }: ${ meta.orderNumber }`

    // return this.repo.save(update)
  }

  public async createOrModifyUnsentUpdate(id: string, type: QuickbooksUpdateTypeEnum, updates?: QuickbooksUpdate[]) {
    const unsent = updates ?
        updates.find((u: QuickbooksUpdate) => !u.sentAt) :
        await this.repo.findUnsentUpdate(id, type, this.restaurantId)

    const update = unsent || new QuickbooksUpdate()
    update.type = type
    update.typeId = id
    update.createdAt = new Date()
    update.status = QuickbooksUpdateStatusEnum.PENDING
    update.restaurantId = this.restaurantId

    return update
  }

  protected adjustUpdateValue(current: number | undefined, previous: string | undefined): string {
    if (!previous && current) {
      return current.toString()
    }
    else if (previous && !current) {
      return previous
    }
    else if (previous && current) {
      return ( parseInt(previous) - current ).toString()
    }
    else {
      return ''
    }
  }

  protected consolidateMetadata(updates: QuickbooksUpdate[]) {
    updates.sort((a: QuickbooksUpdate, b: QuickbooksUpdate) => a.createdAtDate.toTime() - b.createdAtDate.toTime())

    const record: { [index: string]: string } = {}
    for (const update of updates) {
      for (const [ key, value ] of Object(update.meta).entries()) {
        record[key] = value
      }
    }

    return record
  }

  protected async getProductCategoryId(category: string) {
    if(!category || category === '') {
      return Promise.resolve()
    }

    const lineageArray = category.split(' > ')
    const name =  lineageArray[lineageArray.length - 1].trim()
    lineageArray.splice(lineageArray.length - 1, 1)

    const productCategory = await this.productCategoryCollection.findProductCategoryByNameAndLineage(name, lineageArray)

    if(!productCategory) {
      return ''
    }

    return productCategory.id
  }

  protected async getGLCodeById(id: string): Promise<string> {
    if(!id || id === '') {
      return ''
    }

    const glRepository = new GLCodeProductCategoryRepository()
    const glCodeProductCategory = await glRepository.findByProductCategoryIdAndRestaurantId(id, this.restaurantId)

    // recursive lookup here if you dont find the glCode
    if(!glCodeProductCategory || !glCodeProductCategory[0]) {
      const productCategory = await this.productCategoryCollection.get(id)

      if(!productCategory) {
        return ''
      }

      return this.getGLCodeById(productCategory.parentId)
    } else {
      return glCodeProductCategory[0].glCode
    }
  }
}
